import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// A compact top-center header strip: the "Demo" badge (tap for an
// explanation) paired with an "About" navigation link.
//
// Both are anchored at the top of the phone-shell so they appear
// consistently on every main app screen (Home, Project Detail,
// Category, Roots, Profile, Library, Create, Story, Manage) without
// per-page edits. Rendered once from <App/>.
//
// Hidden on operator / transitional surfaces where the demo framing
// does not apply, and on /about itself (the page already provides two
// clear return paths):
//   • /admin         — operator panel, not the public demo
//   • /paused        — maintenance screen, no app chrome
//   • /demo/fresh    — momentary redirect; nothing to annotate
//   • /about         — avoid pointing a link to the page you're on
export default function DemoBadge() {
  const { pathname } = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // Close on outside click or Escape — identical dismissal shape to
  // the native popovers elsewhere in Commons.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Hide on operator / transitional routes and on /about itself.
  const hidden =
    pathname.startsWith("/admin") ||
    pathname.startsWith("/paused") ||
    pathname.startsWith("/demo/fresh") ||
    pathname.startsWith("/about");
  if (hidden) return null;

  return (
    <div className="c-demo-badge-root" ref={rootRef}>
      <div className="c-demo-badge-row">
        <button
          type="button"
          className="c-demo-badge"
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-label="This is a demo of Commons. Tap for details."
          onClick={() => setOpen((v) => !v)}
          data-testid="demo-badge"
        >
          <span className="c-demo-badge-dot" aria-hidden="true" />
          Demo
        </button>
        <span className="c-demo-badge-sep" aria-hidden="true" />
        <button
          type="button"
          className="c-header-about-link"
          onClick={() => nav("/about")}
          data-testid="header-about-link"
        >
          About
        </button>
      </div>
      {open ? (
        <div
          role="dialog"
          aria-label="About this demo"
          className="c-demo-badge-popover"
          data-testid="demo-badge-popover"
        >
          You're exploring a demo of Commons. The projects and participants
          are illustrative, so feel free to try things out.
        </div>
      ) : null}
    </div>
  );
}
